// Messages between dispatch and drivers. One conversation per driver, plus "Everyone" for the whole team.
import { S, drivers, workingDrivers, driverName, isDispatch, threadMsgs, unreadCount } from '../store.js';
import { esc, fmtTime, prettyDate, ymd } from '../util.js';
import { pushState } from '../push.js';

const whenLabel = (ms) => (ymd(ms) === ymd() ? fmtTime(ms) : `${prettyDate(ymd(ms), { weekday: 'short', month: 'short', day: 'numeric' })}, ${fmtTime(ms)}`);
export const badge = (n) => (n ? `<span class="badge" aria-label="${n} unread">${n > 99 ? '99+' : n}</span>` : '');

function bubbles(list) {
  if (!list.length) return '<div class="empty small">No messages yet.</div>';
  let lastDay = '';
  return list.map((m) => {
    const day = ymd(m.at); const head = day !== lastDay ? `<div class="chat-day">${esc(day === ymd() ? 'Today' : prettyDate(day))}</div>` : ''; lastDay = day;
    const me = m.from === S.email;
    return `${head}<div class="msg${me ? ' me' : ''}">${files(m)}${m.text ? `<div class="msg-b">${esc(m.text).replace(/\n/g, '<br>')}</div>` : ''}
      <div class="msg-m">${me ? 'You' : esc(m.fromName || driverName(m.from))}${m.thread === 'all' && !isDispatch() ? ' to everyone' : ''} · ${esc(whenLabel(m.at))}</div></div>`;
  }).join('');
}

const CLIP = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 11.5 12.5 20a5 5 0 0 1-7-7L14 4.5a3.5 3.5 0 0 1 5 5L10.5 18a2 2 0 0 1-3-3L15 7.5"/></svg>';
const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Photos show in the conversation (the app fills in each picture once it has a link); other files are tappable. */
function files(m) {
  if (!m.files?.length) return '';
  return `<div class="msg-files">${m.files.map((f) => (/^image\//.test(f.type)
    ? `<button class="msg-img" data-act="openFile" data-path="${esc(f.path)}" aria-label="Open ${esc(f.name)}"><img data-src-path="${esc(f.path)}" alt="${esc(f.name)}"></button>`
    : `<button class="msg-file" data-act="openFile" data-path="${esc(f.path)}">${CLIP}<span>${esc(f.name)}</span><span class="small muted">${kb(f.size || 0)}</span></button>`)).join('')}</div>`;
}

/** Files picked but not sent yet, so they can be removed before sending. */
export function pendingFiles() {
  return (S.chatFiles || []).map((f, i) => `<span class="chip-file">${esc(f.name)} <button type="button" class="linkbtn" data-act="chatUnpick" data-i="${i}" aria-label="Remove ${esc(f.name)}">x</button></span>`).join('');
}

function composer(thread) {
  return `<div class="chat-picked" id="chatPicked">${pendingFiles()}</div>
    <form class="chat-send" data-thread="${esc(thread)}" onsubmit="return false">
    <label class="btn ghost chat-attach" title="Attach a photo or file" aria-label="Attach a photo or file">${CLIP}<input type="file" id="chatFile" multiple accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.stl" hidden></label>
    <textarea id="chatText" rows="2" maxlength="1000" placeholder="${thread === 'all' ? 'Message everyone' : 'Type a message'}" aria-label="Message"></textarea>
    <button class="btn primary" data-act="chatSend" data-thread="${esc(thread)}" ${S.busy.chatSend ? 'disabled' : ''}>${S.busy.chatSend ? 'Sending…' : 'Send'}</button></form>`;
}

/** Dispatch: conversation list on the left, the open conversation on the right. */
function dispatchChat() {
  const people = [...new Map([...workingDrivers(), ...drivers()].map((d) => [d.email, d])).values()];
  const last = (t) => threadMsgs(t).slice(-1)[0];
  const order = people.slice().sort((a, b) => (last(b.email)?.at || 0) - (last(a.email)?.at || 0));
  // Remember the pick, so the open conversation doesn't jump once its messages are marked read.
  const cur = S.chatWith || (S.chatWith = order.find((d) => unreadCount(d.email))?.email || 'all');
  const row = (t, name) => { const l = last(t); return `<button class="chat-who${t === cur ? ' on' : ''}" data-act="chatOpen" data-thread="${esc(t)}">
      <span class="spread"><b>${esc(name)}</b>${badge(unreadCount(t))}</span>
      <span class="small muted chat-prev">${l ? `${l.from === S.email ? 'You: ' : ''}${esc((l.text || (l.files?.length ? `Sent ${l.files.length === 1 ? 'a file' : `${l.files.length} files`}` : '')).slice(0, 60))}` : 'No messages yet'}</span></button>`; };
  return { cur, html: `<div class="chat-grid">
    <aside class="panel chat-list">${row('all', 'Everyone')}${order.map((d) => row(d.email, d.name || d.email)).join('')}</aside>
    <section class="panel chat-pane"><div class="pad"><h2>${esc(cur === 'all' ? 'Everyone' : driverName(cur))}</h2>
      <div class="small muted">${cur === 'all' ? 'Every driver sees these.' : 'Only this driver and dispatch see these.'}</div></div>
      <div class="chat-log" id="chatLog">${bubbles(threadMsgs(cur))}</div>${composer(cur)}</section></div>` };
}

/** Driver: one conversation with dispatch, with team-wide messages mixed in. */
function driverChat() {
  const list = S.msgs.filter((m) => m.thread === S.email || m.thread === 'all');
  return { cur: S.email, html: `<section class="panel chat-pane chat-solo"><div class="pad spread"><h2>Messages with dispatch</h2><button class="btn ghost small" data-act="closeChat">Back to my route</button></div>
    <div class="chat-log" id="chatLog">${bubbles(list)}</div>${composer(S.email)}</section>` };
}

/** Push notification status for this device, with a button to turn them on. */
function pushBar() {
  const st = pushState();
  const msg = {
    on: '<span class="small muted">Notifications are on for this device.</span>',
    off: '<span>Get a notification on this device when a message comes in, even when the app is closed.</span><button class="btn primary small" data-act="enablePush">Turn on notifications</button>',
    blocked: '<span>Notifications are blocked for this site. Allow them in your browser or phone settings, then reload.</span>',
    'needs-home-screen': '<span>To get notifications on iPhone, tap Share, then <b>Add to Home Screen</b>, open the app from your Home Screen, and turn them on here.</span>',
    unsupported: '<span class="small muted">This browser can\'t show notifications. Messages still show here.</span>',
    'not-set-up': S.me?.role === 'admin' ? '<span class="small muted">Push notifications need the web push key in web/.env (VITE_FCM_VAPID_KEY).</span>' : '',
  }[st];
  return msg ? `<div class="note push-bar${st === 'on' ? ' quiet' : ''}">${msg}</div>` : '';
}

/** Returns { html, read: [threads now on screen] } so the caller can mark them read. */
export function chatPage() {
  const v = isDispatch() && S.view !== 'driver' ? dispatchChat() : driverChat();
  return { html: `<div class="chat">${pushBar()}${v.html}</div>`, read: v.cur === S.email ? [S.email, 'all'] : [v.cur] };
}
